import { useEffect, useRef } from "react";
import { getScrollBehavior } from "@/utils/platform";

interface UseHighlightScrollOptions {
  highlightId: string | null;
  enabled?: boolean;
}

/**
 * Hook to scroll a highlight element into view when selected.
 *
 * Relies on the browser's scrollIntoView behavior and the nearest
 * scrollable ancestor (the markup viewer container) to place the
 * highlight roughly in the middle of the visible area.
 */
export function useHighlightScroll({ highlightId, enabled = true }: UseHighlightScrollOptions) {
  const previousHighlightId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !highlightId || highlightId === previousHighlightId.current) {
      return;
    }

    previousHighlightId.current = highlightId;

    // Defer the DOM lookup slightly so MarkupViewer has time to
    // update decorations for the newly selected highlight.
    const timeout = window.setTimeout(() => {
      const element = document.querySelector(
        `[data-highlight-id="${highlightId}"]`,
      );

      if (!element) {
        return;
      }

      element.scrollIntoView({
        behavior: getScrollBehavior(),
        block: "center",
        inline: "nearest",
      });
    }, 50);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [highlightId, enabled]);
}
